import {
  Component,
  Input,
  OnDestroy,
  OnInit,
  ChangeDetectorRef,
  OnChanges,
  SimpleChanges
} from '@angular/core';
import { Subject, takeUntil } from 'rxjs';
import { MatSnackBar } from '@angular/material/snack-bar';
import { saveAs } from 'file-saver';

import { Client } from '@app/core/types/client';
import { IdentityDocument } from '@app/core/types/client-document';
import { IdentityReportsService } from '@app/core/services/identity-reports.service';
import { NotificationService } from '@app/core/services/notification.service';
import { BrandCodes } from '@app/core/types/brand';
import {
  DOCUMENT_STATUS,
  SLOT_CONFIGS,
  SLOT_KEYS,
  SlotKey,
  VerificationDetail,
  parseDocumentStatus,
  formatDocumentReason,
  ID_VERIFICATION_STATUS,
} from '@app/core/types/id-verification';

const IN_PROGRESS_STATUSES: DOCUMENT_STATUS[] = [
  DOCUMENT_STATUS.PROVIDED,
  DOCUMENT_STATUS.VALIDATING_SIMPLE,
  DOCUMENT_STATUS.VALIDATING_COMPLEX,
];

@Component({
  selector: 'itfg-identity-verification-documents',
  templateUrl: './identity-verification-documents.component.html',
  styleUrl: './identity-verification-documents.component.scss',
  standalone: false,
})
export class IdentityVerificationDocumentsComponent implements OnInit, OnChanges, OnDestroy {
  readonly INVALIDATION_REASONS = ['LOW_QUALITY', 'NOT_APPLICABLE', 'FRAUD', 'OTHER'] as const;
  readonly formatDocumentReason = formatDocumentReason;


  @Input() client!: Client;

  detail: VerificationDetail | null | undefined = undefined;
  selectedDocs: Record<SlotKey, IdentityDocument | null> = {
    front: null, back: null, selfie: null,
  };

  /** Tracks in-progress operations per slot so the UI can show a spinner */
  loadingOp: Record<SlotKey, boolean> = {
    front: false, back: false, selfie: false,
  };

  readonly SLOT_CONFIGS = SLOT_CONFIGS;
  readonly SLOT_KEYS = SLOT_KEYS;

  private readonly _unsubscribe = new Subject<void>();
  docList: IdentityDocument[] = [];
  loadingDocList = false;

  constructor(
    private readonly snackBar: MatSnackBar,
    private readonly identityReportsService: IdentityReportsService,
    private readonly notificationService: NotificationService,
    private readonly cdr: ChangeDetectorRef,
  ) { }

  ngOnInit(): void {
    this.identityReportsService.identityVerificationDetail$
      .pipe(takeUntil(this._unsubscribe))
      .subscribe(detail => {
        this.detail = detail;
        if (detail !== null) {
          this._syncDocList();
        }
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['client'] && this.client?.civilId &&
      changes['client'].currentValue?.civilId !== changes['client'].previousValue?.civilId) {
      this._loadDocList();
    }
  }

  private _loadDocList(): void {

    this.loadingDocList = true;
    this.identityReportsService
      .getIdentityDocumentList$(this.client.civilId, this.client.brand.code as BrandCodes)
      .pipe(takeUntil(this._unsubscribe))
      .subscribe({
        next: docs => {
          this.docList = docs;
        },
        error: err => {
          console.error(err);
          this.loadingDocList = false;
        },
        complete: () => {
          this.loadingDocList = false;
        },
      });
  }

  private _syncDocList(): void {
    const referencedIds = SLOT_KEYS
      .map(key => this.detail?.[SLOT_CONFIGS[key].docKey] as number | null)
      .filter((id): id is number => !!id);

    const allPresent = referencedIds.every(id => this.docList.some(d => d.id === id));

    if (!allPresent) {
      this._loadDocList();
    }
  }
  // ── Slot status helpers ────────────────────────────────────────────

  private _parsed(key: SlotKey) {
    const raw = (this.detail?.[SLOT_CONFIGS[key].statusKey] as string) ?? '';
    return parseDocumentStatus(raw);
  }

  slotStatusClass(key: SlotKey): 'valid' | 'invalid' | 'pending' | 'validating' {
    const base = this._parsed(key).base;
    if (base === DOCUMENT_STATUS.VALID) return 'valid';
    if (base === DOCUMENT_STATUS.INVALID) return 'invalid';
    if (IN_PROGRESS_STATUSES.includes(base)) return 'validating';
    return 'pending';
  }

  slotStatusIcon(key: SlotKey): string {
    const cls = this.slotStatusClass(key);
    const icons = {
      valid: 'check_circle',
      invalid: 'cancel',
      validating: 'hourglass_empty',
      pending: 'hourglass_empty',
    };
    return icons[cls];
  }

  slotStatusLabel(key: SlotKey): string {
    const base = this._parsed(key).base;
    return base ?? '—';
  }

  slotReason(key: SlotKey): string | null {
    const { reason } = this._parsed(key);
    return reason;
  }

  // ── Viewer events ──────────────────────────────────────────────────

  onDocumentSelected(key: SlotKey, doc: IdentityDocument | null): void {
    // Defer to avoid ExpressionChangedAfterItHasBeenCheckedError —
    // this output fires during the child's CD cycle which is inside
    // the parent's current CD pass.
    Promise.resolve().then(() => {
      this.selectedDocs[key] = doc;
      this.cdr.markForCheck();
    });
  }

  // ── Document actions ───────────────────────────────────────────────

  downloadDocument(key: SlotKey): void {
    const doc = this.selectedDocs[key];
    if (!doc) return;

    this.loadingOp[key] = true;
    this.identityReportsService
      .renderIdentityDocumentContent(this.client.civilId, doc.id)
      .pipe(takeUntil(this._unsubscribe))
      .subscribe({
        next: (content: Blob) => {
          const blob = new Blob([content as any], { type: 'image/jpg' });
          saveAs(blob, doc.name || `document-${doc.id}.jpg`);
          this.loadingOp[key] = false;
        },
        error: () => { this.loadingOp[key] = false; },
      });
  }

  rotateDocument(key: SlotKey, clockwise: boolean): void {
    const doc = this.selectedDocs[key];
    if (!doc) return;

    this.loadingOp[key] = true;
    this.identityReportsService
      .rotateIdentityDocument(this.client.civilId, doc.id, clockwise)
      .pipe(takeUntil(this._unsubscribe))
      .subscribe({
        next: () => {
          this.loadingOp[key] = false;
          this.notificationService.showLocalizedSuccessMessage({
            notificationText: 'mi.idCardImageRotateSuccessMessage',
          });
        },
        error: () => { this.loadingOp[key] = false; },
      });
  }

  deleteDocument(key: SlotKey): void {
    const doc = this.selectedDocs[key];
    if (!doc) return;

    this.loadingOp[key] = true;
    this.identityReportsService
      .deleteIdentityDocument(this.client.civilId, doc.id)
      .pipe(takeUntil(this._unsubscribe))
      .subscribe({
        next: () => {
          this.selectedDocs[key] = null;
          this.loadingOp[key] = false;
          this.notificationService.showLocalizedSuccessMessage({
            notificationText: 'mi.idCardImageDeleteSuccessMessage',
          });
        },
        error: () => { this.loadingOp[key] = false; },
      });
  }

  invalidateDocument(key: SlotKey, reason: string): void {
    if (!this.detail) {
      return
    };

    this.loadingOp[key] = true;
    this.identityReportsService
      .invalidateVerificationDocument$(this.detail.id, SLOT_CONFIGS[key].apiType, reason)
      .pipe(takeUntil(this._unsubscribe))
      .subscribe({
        next: () => {
          this.loadingOp[key] = false;
          this.notificationService.showLocalizedSuccessMessage({
            notificationText: 'mi.idCardStatusChangeSuccessMessage',
          });
        },
        error: () => {
          this.loadingOp[key] = false;
        },
      });
  }

  uploadDocument(key: SlotKey, event: Event): void {
    const file = (event.target as HTMLInputElement).files?.[0];
    if (!file) {
      return
    };

    const formData = new FormData();
    formData.append('file', file);

    this.loadingOp[key] = true;
    this.identityReportsService
      .addIdentityDocument$(
        this.client.civilId,
        this.client.brand.code as BrandCodes,
        SLOT_CONFIGS[key].apiType,
        formData,
      )
      .pipe(takeUntil(this._unsubscribe))
      .subscribe({
        next: (newDoc: IdentityDocument) => {
          this.docList = [...this.docList, newDoc];
          this.selectedDocs[key] = newDoc;
          this.loadingOp[key] = false;
          this.notificationService.showLocalizedSuccessMessage({
            notificationText: 'mi.idCardImageUploadSuccessMessage',
          });
          // Reset the input so the same file can be re-uploaded if needed
          (event.target as HTMLInputElement).value = '';
        },
        error: () => { this.loadingOp[key] = false; },
      });
  }

  isActionable(key: SlotKey): boolean {
    if (!this.detail) {
      return false;
    }

    if (this.detail.status !== ID_VERIFICATION_STATUS.MANUAL && this.detail.status !== ID_VERIFICATION_STATUS.IN_PROGRESS) {
      return false;
    };

    if (this.slotStatusClass(key) === 'pending') {
      return false;
    };

    return true;
  }

  ngOnDestroy(): void {
    this._unsubscribe.next();
    this._unsubscribe.complete();
  }
}